import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRight, ShieldCheck, AlertTriangle } from "lucide-react";
import { AfyanexLogo } from "@/components/AfyanexLogo";

export default function AcceptInvite() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    password: "",
    confirm: "",
    name: "",
    phone: "",
    hipaa_acknowledged: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get(`/invites/${token}`)
      .then(({ data }) => {
        setInvite(data);
        setForm((f) => ({ ...f, name: data.invited_name || "" }));
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || "This invite is invalid.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (!form.hipaa_acknowledged) {
      toast.error("Please acknowledge the HIPAA notice to continue");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/invites/${token}/accept`, {
        password: form.password,
        name: form.name,
        phone: form.phone,
        hipaa_acknowledged: true,
      });
      // Auth cookie was set by the server.
      localStorage.setItem("hc_user_profile_cache", JSON.stringify(data.user));
      toast.success("Welcome to the team!");
      window.location.href = data.user.role === "admin" ? "/admin" : "/clients";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not accept invite");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-clinical-bg">
        <Loader2 className="h-5 w-5 animate-spin text-clinical-subtext" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-clinical-bg p-6" data-testid="invite-error-page">
        <div className="bg-white rounded-2xl border border-clinical-border p-8 max-w-md text-center">
          <AlertTriangle className="h-10 w-10 text-destructive mx-auto mb-3" />
          <h1 className="font-heading text-xl font-semibold">Invite unavailable</h1>
          <p className="mt-2 text-sm text-clinical-subtext">{error}</p>
          <p className="mt-4 text-xs text-clinical-subtext">
            Ask your admin to send you a new invite link.
          </p>
          <Link to="/login" className="btn-secondary mt-6 inline-flex" data-testid="invite-error-login-link">
            Go to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-clinical-bg flex items-center justify-center p-6" data-testid="accept-invite-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-clinical-border p-8">
        <div className="mb-6 flex items-center gap-2">
          <AfyanexLogo markSize={28} />
        </div>
        <div className="label-eyebrow">Welcome</div>
        <h1 className="mt-1 font-heading text-2xl font-semibold text-clinical-text">
          You're invited to join {invite.agency_name || "the team"}
        </h1>
        <p className="mt-2 text-sm text-clinical-subtext">
          Set up your account as a <strong>{invite.role}</strong> for{" "}
          <span className="font-medium text-clinical-text">{invite.invited_email}</span>.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="accept-invite-form">
          <div>
            <label className="label-eyebrow mb-1.5 block">Your name</label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="input-clinical"
              data-testid="invite-name-input"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Mobile number</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="555-0100"
              className="input-clinical"
              data-testid="invite-phone-input"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Create a password</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={8}
              placeholder="At least 8 characters"
              className="input-clinical"
              data-testid="invite-password-input"
            />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Confirm password</label>
            <input
              type="password"
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              required
              className="input-clinical"
              data-testid="invite-confirm-input"
            />
          </div>

          <label className="flex items-start gap-2 text-xs text-clinical-text rounded-lg border border-clinical-border bg-clinical-bg p-3">
            <input
              type="checkbox"
              checked={form.hipaa_acknowledged}
              onChange={(e) => setForm({ ...form, hipaa_acknowledged: e.target.checked })}
              className="mt-0.5"
              data-testid="invite-hipaa-checkbox"
            />
            <span>
              <span className="font-medium block mb-0.5">HIPAA notice</span>
              I understand that this app processes Protected Health Information (PHI).
              I'll keep my login confidential, only access information needed for
              client care, and report any suspected breach to my admin immediately.
            </span>
          </label>

          <button
            type="submit"
            disabled={saving}
            className="btn-primary w-full"
            data-testid="invite-submit"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (
              <>
                Accept and sign in <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </form>

        <div className="mt-6 pt-4 border-t border-clinical-border flex items-center gap-2 text-[11px] text-clinical-subtext">
          <ShieldCheck className="h-3.5 w-3.5" />
          Encrypted in transit. Your password is hashed; we never see it in plain text.
        </div>
      </div>
    </div>
  );
}
